import React from 'react';
import { Link } from 'react-router-dom';
import { X, Compass, CheckCircle2, AlertTriangle, MapPin, Home } from 'lucide-react';

/**
 * Modal de explicación del Habita Advisor.
 *
 * Muestra:
 *   - Qué preferencias detectamos (con evidencia).
 *   - Qué restricciones aplicamos.
 *   - Factores que aumentaron el score (positivos).
 *   - Factores que redujeron el score (negativos).
 *   - Alternativas que descartamos.
 *
 * Tono cálido, no técnico. Nunca muestra el score numérico.
 */

const _HUMAN_KEY = {
  quality: 'Calidad de la propiedad',
  city_match: 'Ciudad',
  type_match: 'Tipo de inmueble',
  budget_fit: 'Presupuesto',
  price_band_fit: 'Precio dentro de tu rango observado',
  price_band_far: 'Precio fuera de tu rango habitual',
  rooms_ok: 'Habitaciones',
  rooms_short: 'Habitaciones (menos de lo pedido)',
};

const _HUMAN_PREF = {
  preferred_city: 'Ciudad recurrente en tu recorrido',
  preferred_type: 'Tipo de inmueble que sueles ver',
  price_max: 'Presupuesto máximo observado',
  price_min: 'Presupuesto mínimo observado',
  beds_min: 'Habitaciones mínimas',
  price_band: 'Rango de precio habitual',
  operation: 'Tipo de operación buscada',
  city_or_query: 'Búsqueda inicial',
  type: 'Tipo especificado',
};

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="mb-5" data-testid={testid}>
      <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-habita-dark mb-2">
        <Icon className="w-4 h-4 text-habita" /> {title}
      </h3>
      {children}
    </div>
  );
}

function ChipList({ items, tone = 'ok' }) {
  const cls = tone === 'ok'
    ? 'bg-habita-mint/60 text-habita-dark border-habita/20'
    : tone === 'warn'
      ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-rose-50 text-rose-800 border-rose-200';
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((t, i) => (
        <span key={i} className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`}>{t}</span>
      ))}
    </div>
  );
}

export default function AdvisorExplanation({
  recommendation, item, isSameProperty, onClose, onNavigate, onContact,
}) {
  const rec = recommendation;
  const explanation = item.explanation || {};
  const breakdown = item.breakdown || { positive_factors: [], negative_factors: [] };
  const prefsUsed = (rec.preferences_used || []).filter(p => item.matched_preferences?.includes(p.key));
  const restrictions = rec.restrictions_applied || [];
  const discarded = (rec.discarded || []).filter(d => d.stage === 'ranking').slice(0, 3);

  return (
    <div
      role="dialog"
      aria-label="Explicación de la recomendación"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="advisor-explanation-modal"
    >
      <div
        className="relative w-full sm:max-w-lg sm:rounded-2xl bg-white shadow-2xl overflow-hidden max-h-[90vh] sm:max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-habita text-white grid place-items-center">
              <Compass className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500">Habita Advisor</p>
              <p className="text-sm font-semibold text-habita-dark">¿Por qué te sugerimos esto?</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="advisor-explanation-close"
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            aria-label="Cerrar"
          ><X className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <p className="text-[15px] text-habita-dark leading-relaxed mb-5">
            {explanation.human_readable}
          </p>

          {prefsUsed.length > 0 && (
            <Section title="Detectamos tus preferencias" icon={MapPin} testid="section-prefs">
              <ChipList
                tone="ok"
                items={prefsUsed.map(p => `${_HUMAN_PREF[p.key] || p.key.replace('_', ' ')}${p.value ? `: ${String(p.value).slice(0, 40)}` : ''}`)}
              />
            </Section>
          )}

          {restrictions.length > 0 && (
            <Section title="Criterios que respetamos" icon={CheckCircle2} testid="section-restrictions">
              <ChipList
                tone="ok"
                items={restrictions.map(r => {
                  const label = _HUMAN_PREF[r.key] || r.key;
                  const opLabel = { eq: '=', gte: '≥', lte: '≤' }[r.op] || r.op;
                  return `${label} ${opLabel} ${String(r.value).slice(0, 30)}`;
                })}
              />
            </Section>
          )}

          {breakdown.positive_factors?.length > 0 && (
            <Section title="Por qué es una buena coincidencia" icon={CheckCircle2} testid="section-positives">
              <ul className="space-y-2">
                {breakdown.positive_factors.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-habita-dark">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{_HUMAN_KEY[f.key] || f.key}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {breakdown.negative_factors?.length > 0 && (
            <Section title="Puntos a considerar" icon={AlertTriangle} testid="section-negatives">
              <ul className="space-y-2">
                {breakdown.negative_factors.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <span>{_HUMAN_KEY[f.key] || f.key}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {discarded.length > 0 && (
            <Section title="Otras opciones que consideramos" icon={Home} testid="section-alternatives">
              <ul className="space-y-1.5 text-xs text-gray-600">
                {discarded.map((d, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-gray-400">→</span>
                    <span>{d.reason}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2">
            {!isSameProperty && item.item_id && (
              <Link
                to={`/propiedad/${item.item_id}`}
                onClick={() => { onNavigate?.(); onClose(); }}
                className="flex-1 h-11 rounded-full bg-habita text-white font-semibold text-sm inline-flex items-center justify-center hover:opacity-90 transition"
                data-testid="advisor-explanation-visit-btn"
              >
                Ver esta propiedad
              </Link>
            )}
            <button
              onClick={onClose}
              className="flex-1 h-11 rounded-full border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition"
              data-testid="advisor-explanation-thanks-btn"
            >
              Gracias, cerrar
            </button>
          </div>
          <p className="mt-4 text-[10px] text-gray-400 text-center">
            Recomendación determinística · estrategia {rec.strategy} · {rec.elapsed_ms}ms
          </p>
        </div>
      </div>
    </div>
  );
}
